import {
  AiMappingsRow,
  BulkMappingLabel,
  EffectiveMappingItem,
  EffectiveSpeciesListItem,
  EnabledModelItem,
  ModelResponseDto,
  ProjectMappingCreateRequest,
  ProjectMappingUpdateRequest,
  TargetKind,
  TaxonomyResponseDto,
} from "../common/ObjectTypes";

// --- Combined-view model filter (AKL-729) -----------------------------------
// The AI Mappings model dropdown is now an optional filter rather than a
// precondition: a specific `model_id` narrows exactly like the old
// single-model view, and the sentinel below means "every enabled model,
// combined" (the new default).
export const ALL_MODELS_FILTER = "__all__" as const;
export type ModelFilter = number | typeof ALL_MODELS_FILTER;

/**
 * Resolve the `model_id` query param `getProjectMappings` should send for a
 * given filter value — `undefined` (omitted) for the "All models" sentinel,
 * the numeric id otherwise. The API already computes the combined-vs-single
 * view server-side, so this is the only branch point the container needs.
 * @param filter the current model filter value
 */
export const resolveMappingsModelId = (
  filter: ModelFilter,
): number | undefined => (filter === ALL_MODELS_FILTER ? undefined : filter);

/**
 * Resolve the model filter to restore from a persisted `localStorage` value
 * on load: no stored value, or an explicitly stored "All models" sentinel,
 * both resolve to "All models"; a stored specific `model_id` resolves to
 * itself only if still present in the freshly-fetched enabled-models list —
 * otherwise (a model that's since been... there's no "disable" action yet,
 * but defensively handle it anyway) falls back to "All models" silently, no
 * error toast.
 * @param stored the raw `localStorage` value, or null if unset
 * @param models the just-fetched enabled-models list
 */
export const resolvePersistedModelFilter = (
  stored: string | null,
  models: EnabledModelItem[],
): ModelFilter => {
  if (stored == null || stored === ALL_MODELS_FILTER) return ALL_MODELS_FILTER;
  const storedId = Number(stored);
  return models.some((m) => m.model_id === storedId)
    ? storedId
    : ALL_MODELS_FILTER;
};

// --- Model enablement table (disable follow-up) -----------------------------

export type ModelEnablementStatus = "enabled" | "disabled" | "not_enabled";

export type ModelEnablementRow = {
  model_id: number;
  name: string;
  model_type: string;
  model_version: string | null;
  /** The model's live global status ("active"/"draft"/etc.) — display only,
   *  never a gate. Assumed "active" for a row sourced only from the global
   *  active-only model list (it has no other status to report). */
  globalStatus: string | null;
  enablementStatus: ModelEnablementStatus;
  enabledAt: string | null;
  disabledAt: string | null;
};

/**
 * Merge the global active-model catalogue with this project's enabled-model
 * list into one row per model, for the enablement table: a model only in the
 * global list is "not enabled" (action: Enable); a model in the enabled list
 * with no `disabled_at` is "enabled" (action: Disable); one with
 * `disabled_at` set is "disabled" (action: Enable, i.e. re-enable). An
 * enabled-but-globally-inactive model (absent from the active-only global
 * list) still gets a row, sourced from the enabled list alone — never
 * hidden, matching this feature's existing inactive-model badge rule.
 * Sorted by name for a stable, scannable table.
 * @param globalModels every globally-active model (`getModels()`)
 * @param enabledModels every model this project has enabled (enabled or disabled)
 */
export const buildModelEnablementRows = (
  globalModels: ModelResponseDto[],
  enabledModels: EnabledModelItem[],
): ModelEnablementRow[] => {
  const byId = new Map<number, ModelEnablementRow>();
  globalModels.forEach((m) => {
    byId.set(m.model_id, {
      model_id: m.model_id,
      name: m.name,
      model_type: m.model_type,
      model_version: m.model_version,
      globalStatus: "active",
      enablementStatus: "not_enabled",
      enabledAt: null,
      disabledAt: null,
    });
  });
  enabledModels.forEach((m) => {
    byId.set(m.model_id, {
      model_id: m.model_id,
      name: m.name,
      model_type: m.model_type,
      model_version: m.model_version,
      globalStatus: m.status,
      enablementStatus: m.disabled_at != null ? "disabled" : "enabled",
      enabledAt: m.enabled_at,
      disabledAt: m.disabled_at,
    });
  });
  return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
};

/**
 * Resolve the `ai_model_id` the add/edit-mapping modal should stamp onto a
 * new mapping (AKL-729 §4). An existing row (`edit`/`addOverride`) always
 * carries its own `ai_model_id` already, regardless of the container's
 * current filter — `addOverride` in particular must never fall back to the
 * filter, since in the combined view a row can belong to a different model
 * than whatever's selected there. Only brand-new `add` mode has no implicit
 * model: a specific filter prefills it (today's behavior, unchanged); the
 * "All models" sentinel has none, so the caller's own explicit model-picker
 * selection is used instead.
 * @param mode the modal's current mode
 * @param modelFilter the container's current model filter
 * @param row the row being edited/overridden (null in `add` mode)
 * @param selectedAddModelId the add-mode model picker's current selection
 */
export const resolveAddMappingModelId = (
  mode: "add" | "edit" | "addOverride",
  modelFilter: ModelFilter,
  row: AiMappingsRow | null,
  selectedAddModelId: number | null,
): number | null => {
  if (mode === "addOverride") return row?.ai_model_id ?? null;
  if (mode === "edit") return null;
  return modelFilter !== ALL_MODELS_FILTER ? modelFilter : selectedAddModelId;
};

/**
 * Editable state backing the add/edit mapping modal. Kept flat and UI-agnostic
 * so the request-builders below can be unit-tested without React.
 */
export type MappingFormState = {
  raw_label: string;
  target_kind: TargetKind;
  taxon_id: number | null;
  display_name_override: string;
};

/**
 * Which row actions are available for an effective-mapping row, derived purely
 * from its `source`/`target_kind`/`deletable` fields (no permission check — the
 * caller AND-gates these with `canManage`).
 */
export type MappingRowActions = {
  canEdit: boolean; // project rows only (PATCH)
  canExclude: boolean; // exclude a non-excluded taxon row (project PATCH or global POST)
  canDelete: boolean; // project-added row with no global default
  canAddOverride: boolean; // global fallback row — create a project override
  canReset: boolean; // project row shadowing a global default (POST .../reset)
};

/**
 * A human label for a taxonomy row: common name → scientific → species → id.
 * Taxon names are data and are never translated.
 * @param tax the taxonomy row
 * @returns a display label
 */
export const taxonLabel = (tax: TaxonomyResponseDto): string =>
  tax.common_name_english || tax.scientific_name || tax.species || `#${tax.id}`;

/**
 * Validate the mapping form the way the API validators do, so we never
 * round-trip a request the API will reject.
 * @param form the current form state
 * @returns an error message, or null when valid
 */
export const mappingFormError = (form: MappingFormState): string | null => {
  if (!form.raw_label || form.raw_label.trim() === "") {
    return "Raw label is required";
  }
  if (form.target_kind === "taxon" && form.taxon_id == null) {
    return "Select a taxon";
  }
  return null;
};

/**
 * Build a create request from the form, honouring the API's field rules:
 * `excluded` omits `taxon_id`; an empty override is sent as null.
 * @param aiModelId the model the mapping belongs to
 * @param form the current form state
 */
export const buildMappingCreateRequest = (
  aiModelId: number,
  form: MappingFormState,
): ProjectMappingCreateRequest => {
  const override = form.display_name_override.trim();
  if (form.target_kind === "excluded") {
    return {
      ai_model_id: aiModelId,
      raw_label: form.raw_label.trim(),
      target_kind: "excluded",
    };
  }
  return {
    ai_model_id: aiModelId,
    raw_label: form.raw_label.trim(),
    target_kind: "taxon",
    taxon_id: form.taxon_id,
    display_name_override: override === "" ? null : override,
  };
};

/**
 * Build an update request from the form. Excluding clears the taxon server-side;
 * a taxon edit carries the taxon id and (cleared-or-set) override.
 * @param form the current form state
 */
export const buildMappingUpdateRequest = (
  form: MappingFormState,
): ProjectMappingUpdateRequest => {
  const override = form.display_name_override.trim();
  if (form.target_kind === "excluded") {
    return { target_kind: "excluded" };
  }
  return {
    target_kind: "taxon",
    taxon_id: form.taxon_id,
    display_name_override: override === "" ? null : override,
  };
};

/**
 * Derive the available row actions for an effective-mapping row.
 * @param item the effective-mapping row
 */
export const mappingRowActions = (
  item: EffectiveMappingItem,
): MappingRowActions => {
  const isProject = item.source === "project" && item.id != null;
  const isExcluded = item.target_kind === "excluded";
  return {
    canEdit: isProject,
    // Only offer the one-click Exclude shortcut on a non-excluded row.
    canExclude: !isExcluded,
    canDelete: isProject && item.deletable,
    canAddOverride: item.source === "global",
    // Reset only applies to a project row that shadows a global default —
    // a project row with no global default is permanently removed via Delete
    // instead, so the two actions are mutually exclusive per row.
    canReset: isProject && !item.deletable,
  };
};

/**
 * Derive the available row actions for a merged label-first row (AKL-715),
 * mirroring `mappingRowActions` so the two never drift independently — this is
 * the single source of truth `AiMappingsLabelTable` reads from.
 * @param row the merged row
 */
export const aiMappingsRowActions = (row: AiMappingsRow): MappingRowActions => {
  const isLabelRow = row.raw_label != null;
  const isProject =
    isLabelRow && row.source === "project" && row.mapping_id != null;
  const isExcluded = row.target_kind === "excluded";
  return {
    canEdit: isProject,
    canExclude: isLabelRow && !isExcluded,
    canDelete: isProject && row.deletable,
    canAddOverride: isLabelRow && row.source === "global",
    canReset: isProject && row.canReset,
  };
};

/**
 * Merge a set of effective mappings with the project's effective species
 * list into one label-first row set (AKL-715, combined-view since AKL-729): a
 * row per raw label, plus — only when `includeManualRows` is true — a
 * manual-only row (no raw label, "added by hand") for any explicit
 * species-list entry (`species_list_item_id != null`) not already reached by
 * one of the given mappings. `mappings` may span every enabled model at once
 * (the default combined view) or just one (a specific filter selection) —
 * each row carries its own `ai_model_id`, so callers can attribute it
 * regardless. A species-list item reached only via some *other* model's
 * mapping not included in `mappings` (`species_list_item_id == null`) is
 * dropped entirely rather than shown as manual — such an item was never
 * added by hand. Sorted by resolved display name (falling back to the raw
 * label) for a stable species-ordered view. Client-side only — never sent
 * to/from the API.
 *
 * `includeManualRows` should be false whenever the caller has filtered down
 * to a single model: a manually-added species belongs to the project as a
 * whole, not to any one model, so it has no "default label" to show
 * alongside when the view is scoped to one model's own catalogue — it only
 * belongs in the all-models combined view. Defaults to true (combined view)
 * to match this function's original single-view behavior.
 * @param mappings effective mappings currently in view (one model or all)
 * @param speciesItems the project's effective species list (project-wide)
 * @param includeManualRows whether to interleave manually-added species rows
 *   (true for the "all models" combined view, false for a single-model filter)
 */
export const mergeMappingAndSpeciesRows = (
  mappings: EffectiveMappingItem[],
  speciesItems: EffectiveSpeciesListItem[],
  includeManualRows: boolean = true,
): AiMappingsRow[] => {
  const byTaxonId = new Map<number, EffectiveSpeciesListItem>();
  speciesItems.forEach((item) => byTaxonId.set(item.taxon_id, item));
  const consumedTaxonIds = new Set<number>();

  const labelRows: AiMappingsRow[] = mappings.map((item) => {
    const taxonId = item.resolved_taxon_id ?? null;
    const speciesItem = taxonId != null ? byTaxonId.get(taxonId) : undefined;
    if (speciesItem && taxonId != null) consumedTaxonIds.add(taxonId);
    return {
      rowId:
        item.id != null
          ? `mapping-${item.id}`
          : `global-${item.ai_model_id}-${item.raw_label}`,
      taxon_id: taxonId,
      raw_label: item.raw_label,
      resolved_display_name: item.resolved_display_name ?? null,
      display_name_override: item.display_name_override ?? null,
      scientific_name: speciesItem?.scientific_name ?? null,
      target_kind: item.target_kind,
      source: item.source,
      manual: false,
      deletable: item.deletable,
      canReset: mappingRowActions(item).canReset,
      mapping_id: item.id ?? null,
      ai_model_id: item.ai_model_id,
      species_list_item_id: speciesItem?.species_list_item_id ?? null,
      sources: speciesItem?.sources ?? [],
      // "Customized" means this raw label has a project-level override
      // mapping (pointed at a species, or excluded, by this project) rather
      // than resolving via the model's own global default — independent of
      // whether a display-name override was also set.
      customized: item.source === "project",
    };
  });

  // Only a genuine explicit species-list entry (species_list_item_id != null)
  // becomes a manual/"added by hand" row here, and only in the combined view
  // (includeManualRows) — a single-model filter should show that model's own
  // labels/overrides only, not project-wide manual additions that have
  // nothing to do with this specific model. An unconsumed item with no
  // species_list_item_id is present only via some *other* model's mapping
  // (per EffectiveSpeciesListItem's own doc comment) — irrelevant to the
  // currently selected model, not something a project admin added by hand,
  // so it's dropped rather than mislabeled.
  const manualRows: AiMappingsRow[] = includeManualRows
    ? speciesItems
        .filter(
          (item) =>
            !consumedTaxonIds.has(item.taxon_id) &&
            item.species_list_item_id != null,
        )
        .map((item) => ({
          rowId: `species-${item.species_list_item_id ?? item.taxon_id}`,
          taxon_id: item.taxon_id,
          raw_label: null,
          resolved_display_name: item.display_name ?? null,
          display_name_override: item.label_override ?? null,
          scientific_name: item.scientific_name ?? null,
          target_kind: null,
          source: "manual" as const,
          manual: true,
          deletable: false,
          canReset: false,
          mapping_id: null,
          ai_model_id: null,
          species_list_item_id: item.species_list_item_id ?? null,
          sources: item.sources,
          // A manual row has no raw-label mapping to override in the first
          // place — it's the explicit species-list addition itself, not a
          // project override of some model's label.
          customized: false,
        }))
    : [];

  return [...labelRows, ...manualRows].sort((a, b) => {
    const nameA = (a.resolved_display_name || a.raw_label || "").toLowerCase();
    const nameB = (b.resolved_display_name || b.raw_label || "").toLowerCase();
    return nameA.localeCompare(nameB);
  });
};

/**
 * Filter+annotate a row against a search query, matching raw label, resolved
 * species name (common or scientific), or display-name override — not raw
 * label alone, since manual-only rows have no raw label to match.
 * @param row the merged row
 * @param query the current search text (case-insensitive substring match)
 * @returns the row annotated with `matchedField`, or null if it doesn't match
 */
export const matchesSearch = (
  row: AiMappingsRow,
  query: string,
): AiMappingsRow | null => {
  const term = query.trim().toLowerCase();
  if (term === "") return row;

  if (row.raw_label?.toLowerCase().includes(term)) {
    return { ...row, matchedField: "raw_label" };
  }
  if (
    row.resolved_display_name?.toLowerCase().includes(term) ||
    row.scientific_name?.toLowerCase().includes(term)
  ) {
    return { ...row, matchedField: "species" };
  }
  if (row.display_name_override?.toLowerCase().includes(term)) {
    return { ...row, matchedField: "display_name" };
  }
  return null;
};

/**
 * Build the `labels` array for a bulk project-mapping upsert (AKL-751) — the
 * `(model, raw label)` pairs a bulk Exclude or "Point all at one species"
 * should apply to. Serves both actions: they differ only in the request's
 * `target_kind`/`taxon_id`, which the caller supplies.
 *
 * A row is skipped when it has no `raw_label` or no `ai_model_id` — a manual
 * ("added by hand") row has no model label to attach a mapping to, exactly as
 * before. There is deliberately no create-vs-update decision here any more:
 * each entry is a server-side upsert keyed on
 * `(project_id, ai_model_id, raw_label)`, and clearing-or-keeping
 * `display_name_override` is likewise the server's job, so the client no
 * longer needs to know a row's per-row mapping state. No de-duplication
 * either: the merged row set already holds one row per `(model, label)`, and
 * the API de-duplicates the list regardless.
 *
 * An empty result is the caller's signal to fire no request at all — the API
 * rejects an empty `labels` with a 422.
 * @param rows the selected rows
 * @returns one label pair per actionable row, in selection order
 */
export const buildBulkMappingLabels = (
  rows: AiMappingsRow[],
): BulkMappingLabel[] => {
  const labels: BulkMappingLabel[] = [];
  rows.forEach((row) => {
    if (row.raw_label == null || row.ai_model_id == null) return;
    labels.push({ ai_model_id: row.ai_model_id, raw_label: row.raw_label });
  });
  return labels;
};

/**
 * Build the `labels` array for a bulk Exclude — `buildBulkMappingLabels`
 * narrowed to rows that are not already excluded.
 *
 * Extracted in AKL-751 round 2 because two callers now need it: `runPending`,
 * which sends the request, and the toolbar button, which resolves the count for
 * its confirmation before opening it. Inlining the predicate in both would let
 * the confirmation's count drift from what the action actually does.
 * @param rows the selected rows
 * @returns one label pair per excludable row, in selection order
 */
export const buildBulkExcludeLabels = (
  rows: AiMappingsRow[],
): BulkMappingLabel[] =>
  buildBulkMappingLabels(rows.filter((row) => row.target_kind !== "excluded"));

/**
 * Build the `labels` array for a bulk "Reset to model default" (AKL-751 round
 * 2) — the `(model, raw label)` pairs whose project override should be deleted
 * so the model's global default stops being shadowed.
 *
 * `canReset` is the gate, matching the per-row Reset button exactly: it is
 * true only for a project override that has a global default behind it. A
 * custom project mapping has nothing to fall back to, so "resetting" it would
 * be a plain delete — the per-row UI hides Reset for those rows (they are
 * removed with Delete instead), the API skips them with `no_global_default`,
 * and filtering them here means a select-all never even sends them. The
 * `raw_label`/`ai_model_id` checks additionally drop manual ("added by hand")
 * rows, which have no model label at all.
 *
 * Kept separate from `buildBulkMappingLabels` rather than parameterised: the
 * two share a return shape but not a predicate, and that function already has
 * two callers whose behavior must not shift.
 *
 * An empty result is the caller's signal to fire no request at all — the API
 * rejects an empty `labels` with a 422.
 * @param rows the selected rows
 * @returns one label pair per resettable row, in selection order
 */
export const buildBulkResetLabels = (
  rows: AiMappingsRow[],
): BulkMappingLabel[] => {
  const labels: BulkMappingLabel[] = [];
  rows.forEach((row) => {
    if (!row.canReset) return;
    if (row.raw_label == null || row.ai_model_id == null) return;
    labels.push({ ai_model_id: row.ai_model_id, raw_label: row.raw_label });
  });
  return labels;
};
